'use client';

import React from 'react';

import { useAuth } from 'src/lib/store/auth-store';

import { SplashScreen } from 'src/components/loading-screen';

interface AppInitializationGateProps {
  children: React.ReactNode;
}

/**
 * App Initialization Gate
 *
 * Manages the app-wide loading state during authentication initialization.
 * Ensures the app doesn't render until auth is ready.
 *
 * Loading States:
 * - Auth loading: Show loading screen
 * - Auth error: Let auth system handle (redirect to login)
 * - Ready: Render app
 */
export function AppInitializationGate({ children }: AppInitializationGateProps) {
  const { state } = useAuth();

  // Determine if we should show loading screen
  const isLoading = React.useMemo(() => state === 'loading', [state]);

  // Show loading screen while initializing
  if (isLoading) {
    return (
      <SplashScreen
        sx={{
          '& .MuiTypography-root': {
            color: 'text.primary'
          }
        }}
      />
    );
  }

  // App is ready to render
  return <>{children}</>;
}

/**
 * Hook to check if the app is fully initialized
 *
 * Useful for components that need to know when auth is ready
 */
export function useAppInitializationStatus() {
  const { state } = useAuth();

  return React.useMemo(() => {
    const isAuthReady = state === 'authenticated' || state === 'unauthenticated';

    return {
      isAuthReady,
      isFullyInitialized: isAuthReady,
      authState: state,
    };
  }, [state]);
} 